import { useAui, useAuiState, useThreadViewportStore } from '@assistant-ui/react';
import { useChatRuntime } from 'components/Chat/AssistantChatRuntime';
import useBottomScrollAnchor from 'hooks/useBottomScrollAnchor';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMessageText, hasAssistantStreamingOutput } from 'utils/chat/chatHelpers';

const THREAD_BOTTOM_STICK_THRESHOLD_PX = 80;

/**
 * Presentation view model for the complete chat panel.
 *
 * Use from `ChatPanelRuntimeContent` to compose assistant-ui state, runtime errors,
 * suggestion controls, composer state, loading placeholders, and scroll behavior into
 * `ChatPanelView` props. Data loading and websocket request execution live in lower hooks.
 *
 * @param {Object} props
 * @param {boolean} props.hasOlderMessages Whether older pages can be loaded.
 * @param {boolean} props.hideBackButton Whether the header back button should be hidden.
 * @param {boolean} props.hideComposer Whether the composer should be hidden.
 * @param {boolean} props.isConnected Whether websocket-backed send controls are available.
 * @param {boolean} props.isLoadingOlderMessages Whether pagination is currently loading.
 * @param {Function} [props.onBack] Header back action.
 * @param {Function} [props.onLoadOlderMessages] Loads the next older message page.
 * @param {Function} [props.onSuggestionSelected] Called before a prompt suggestion is sent.
 * @param {boolean} props.shouldScrollToBottomOnInitialize Initial thread scroll behavior.
 * @param {boolean} [props.showSuggestions=false] Whether prompt suggestions should be shown.
 * @param {Array<Object>} [props.suggestions=[]] Prompt suggestion models.
 * @param {Object} props.viewModel Header/view copy for this chat panel.
 * @returns {Object} Props grouped for `ChatPanelView`.
 */
const useChatPanelViewModel = ({
    hasOlderMessages,
    hideBackButton,
    hideComposer,
    isConnected,
    isLoadingOlderMessages,
    onBack,
    onLoadOlderMessages,
    onSuggestionSelected,
    shouldScrollToBottomOnInitialize,
    showSuggestions = false,
    suggestions = [],
    viewModel,
}) => {
    const { t } = useTranslation('assignment');
    const aui = useAui();
    const threadViewportStore = useThreadViewportStore();
    const composerText = useAuiState((state) => state.composer.text);
    const messages = useAuiState((state) => state.thread.messages);
    const isRunning = useAuiState((state) => state.thread.isRunning);
    const { chatError, clearChatError, isRetrying, manualAssistantDraft, retryLastRequest } = useChatRuntime();
    const threadViewportRef = useRef(null);
    const suggestionsOverlayRef = useRef(null);
    const hasTriggeredTopLoadRef = useRef(false);
    const pendingScrollRestoreRef = useRef(null);
    const [suggestionsOverlayHeight, setSuggestionsOverlayHeight] = useState(0);
    const [hasMeasuredSuggestionsOverlay, setHasMeasuredSuggestionsOverlay] = useState(false);
    const [showTopScrollGradient, setShowTopScrollGradient] = useState(false);
    const headerTitle = viewModel?.header?.title;
    const headerInfoTooltip = viewModel?.header?.infoTooltip;
    const hasStreamingOutput = hasAssistantStreamingOutput({ isRunning, messages });
    const hasBlockingChatError = Boolean(chatError?.actionType);
    const lastMessage = messages[messages.length - 1];
    const bottomScrollAnchorKey = `${messages.length}:${lastMessage?.id || ''}:${getMessageText(lastMessage?.content || []).length}:${manualAssistantDraft.length}`;

    const { updateBottomScrollAnchor } = useBottomScrollAnchor({
        containerRef: threadViewportRef,
        contentKey: bottomScrollAnchorKey,
        isActive: isRunning || Boolean(manualAssistantDraft),
        isPaused: isLoadingOlderMessages,
        thresholdPx: THREAD_BOTTOM_STICK_THRESHOLD_PX,
    });

    const scrollThreadToBottom = () => {
        threadViewportStore.getState().scrollToBottom({ behavior: 'smooth' });
    };

    const sendComposerMessage = () => {
        if (isRunning || isRetrying || !isConnected) return;

        clearChatError();
        scrollThreadToBottom();
        aui.composer().send();
    };

    const sendSuggestion = (text) => {
        onSuggestionSelected?.();
        aui.composer().setText(text);
        sendComposerMessage();
    };

    useLayoutEffect(() => {
        if (!pendingScrollRestoreRef.current || !threadViewportRef.current || isLoadingOlderMessages) return;

        const { previousScrollHeight, previousScrollTop } = pendingScrollRestoreRef.current;
        const nextScrollHeight = threadViewportRef.current.scrollHeight;

        threadViewportRef.current.scrollTop = previousScrollTop + (nextScrollHeight - previousScrollHeight);
        pendingScrollRestoreRef.current = null;
    }, [isLoadingOlderMessages, messages.length]);

    useLayoutEffect(() => {
        const element = suggestionsOverlayRef.current;

        if (!showSuggestions || suggestions.length === 0 || !element) {
            setSuggestionsOverlayHeight(0);
            setHasMeasuredSuggestionsOverlay(false);
            return;
        }

        const resizeObserver = new ResizeObserver(([entry]) => {
            const nextHeight = entry.target.offsetHeight || 0;
            setSuggestionsOverlayHeight((currentHeight) => Math.max(currentHeight, nextHeight));
            setHasMeasuredSuggestionsOverlay(true);
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [showSuggestions, suggestions.length]);

    const shouldDelayThreadContent = showSuggestions && suggestions.length > 0 && !hasMeasuredSuggestionsOverlay;

    useEffect(() => {
        const element = threadViewportRef.current;
        if (!element) return;

        setShowTopScrollGradient(element.scrollTop > 0);
    }, [messages.length, shouldDelayThreadContent]);

    const onThreadScroll = async (event) => {
        updateBottomScrollAnchor(event.currentTarget);
        setShowTopScrollGradient(event.currentTarget.scrollTop > 0);

        if (event.currentTarget.scrollTop > 48) {
            hasTriggeredTopLoadRef.current = false;
            return;
        }

        if (!hasOlderMessages || isLoadingOlderMessages || !onLoadOlderMessages) return;
        if (isRunning || isRetrying) return;
        if (hasTriggeredTopLoadRef.current) return;

        hasTriggeredTopLoadRef.current = true;
        pendingScrollRestoreRef.current = {
            previousScrollHeight: event.currentTarget.scrollHeight,
            previousScrollTop: event.currentTarget.scrollTop,
        };
        await onLoadOlderMessages();
    };

    return {
        composer: {
            isVisible: !hideComposer,
            props: {
                disabled: hasBlockingChatError || isRunning || isRetrying || !isConnected,
                disabledTooltip: isRunning ? t('chat.ai-responding', 'Please wait while the AI is responding.') : '',
                placeholder: t('chat.ask-a-question', 'Ask a question about this feedback'),
                value: composerText,
                onChange: (value) => aui.composer().setText(value),
                onSend: sendComposerMessage,
            },
        },
        error: {
            action: chatError?.actionType
                ? {
                      icon: chatError.icon,
                      label: chatError.actionLabel,
                  }
                : null,
            message: chatError?.message || '',
            onAction: () => {
                clearChatError();
                if (chatError?.actionType === 'refresh') {
                    window.location.reload();
                    return;
                }

                retryLastRequest?.();
            },
        },
        header: {
            infoTooltip: headerInfoTooltip,
            isTitleCentered: !hideBackButton,
            onBack,
            showBackButton: !hideBackButton,
            title: headerTitle,
        },
        suggestions: {
            disabled: hasBlockingChatError || isRunning || isRetrying || !isConnected,
            isVisible: showSuggestions && suggestions.length > 0,
            items: suggestions,
            onSelect: sendSuggestion,
            overlayRef: suggestionsOverlayRef,
        },
        thread: {
            isLoadingOlderMessages,
            messagesProps: {
                bottomInset: suggestionsOverlayHeight,
                hasLoadingPlaceholder: (isRunning && !hasStreamingOutput) || (isRetrying && !manualAssistantDraft),
                isRunning,
                messages,
            },
            onScroll: onThreadScroll,
            shouldDelayContent: shouldDelayThreadContent,
            showTopScrollGradient,
            viewportProps: {
                ref: threadViewportRef,
                turnAnchor: 'bottom',
                autoScroll: true,
                scrollToBottomOnRunStart: false,
                scrollToBottomOnInitialize: shouldScrollToBottomOnInitialize,
                scrollToBottomOnThreadSwitch: false,
            },
        },
    };
};

export default useChatPanelViewModel;
